import { Pause, Play } from 'lucide-react-native'
import { useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { Button as PaperButton, Text, useTheme } from 'react-native-paper'
import DateTimePicker from '@/components/DateTimePicker'
import {
    formatPauseDate,
    getMaxPauseDate,
    getNextDayStart,
    getNextWeekStart,
    isTrackingPaused,
} from '@/lib/tracking-pause'

interface TrackingPauseSectionProps {
    pausedUntil: string | null
    onSetPause: (date: string | null) => void
    disabled?: boolean
    weekStartDay?: number
}

export function TrackingPauseSection({
    pausedUntil,
    onSetPause,
    disabled = false,
    weekStartDay,
}: TrackingPauseSectionProps) {
    const theme = useTheme()
    const paused = isTrackingPaused(pausedUntil)
    const [showDatePicker, setShowDatePicker] = useState(false)

    if (paused && pausedUntil) {
        return (
            <View
                style={[styles.pausedBanner, { backgroundColor: theme.colors.secondaryContainer }]}
            >
                <View style={styles.pausedContent}>
                    <Pause size={18} color={theme.colors.onSecondaryContainer} />
                    <View style={styles.pausedText}>
                        <Text
                            variant="titleSmall"
                            style={{ color: theme.colors.onSecondaryContainer }}
                        >
                            Tracking paused
                        </Text>
                        <Text
                            variant="bodySmall"
                            style={{ color: theme.colors.onSecondaryContainer }}
                        >
                            Until {formatPauseDate(pausedUntil)}
                        </Text>
                    </View>
                </View>
                <PaperButton
                    mode="contained"
                    compact
                    icon={({ size, color }) => <Play size={size - 4} color={color} />}
                    onPress={() => onSetPause(null)}
                    disabled={disabled}
                >
                    Resume
                </PaperButton>
            </View>
        )
    }

    return (
        <View style={styles.container}>
            <View style={styles.header}>
                <Pause size={16} color={theme.colors.onSurfaceVariant} />
                <Text variant="titleSmall" style={{ color: theme.colors.onSurface }}>
                    Pause Tracking
                </Text>
            </View>
            <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                Temporarily stop automatically recording working time at job locations. Manual
                clock-in will still work.
            </Text>
            <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                Pause Until:
            </Text>
            <View style={styles.buttonRow}>
                <PaperButton
                    mode="outlined"
                    compact
                    onPress={() => onSetPause(getNextDayStart())}
                    disabled={disabled}
                >
                    Tomorrow
                </PaperButton>
                <PaperButton
                    mode="outlined"
                    compact
                    onPress={() => onSetPause(getNextWeekStart(weekStartDay))}
                    disabled={disabled}
                >
                    Next Week
                </PaperButton>
                <PaperButton
                    mode="outlined"
                    compact
                    onPress={() => setShowDatePicker(true)}
                    disabled={disabled}
                >
                    Custom Date...
                </PaperButton>
            </View>
            {showDatePicker && (
                <DateTimePicker
                    value={null}
                    mode="date"
                    minimumDate={new Date()}
                    maximumDate={getMaxPauseDate()}
                    onChange={date => {
                        setShowDatePicker(false)
                        if (date) {
                            const start = new Date(date)
                            start.setHours(0, 0, 0, 0)
                            onSetPause(start.toISOString())
                        }
                    }}
                />
            )}
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        gap: 8,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    buttonRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    pausedBanner: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 12,
        borderRadius: 8,
        gap: 12,
    },
    pausedContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        flex: 1,
    },
    pausedText: {
        gap: 2,
    },
})
